import React from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLocale } from '../../context/LocaleContext';
import LocaleSwitch from './LocaleSwitch';
import { useState } from 'react';

const UserLayout = () => {
  const { user, isAuthenticated, logout, userRole } = useAuth();
  const { t, dir, lang } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [phoneShown, setPhoneShown] = useState(false);

  const isUser = userRole === 'user';
  const isAdmin = userRole === 'admin';
  const isOrganizer = userRole === 'organizer';
  const isAgent = userRole === 'agent';

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isHome = location.pathname === '/';

  return (
    <div dir={dir} className="public-site min-h-screen relative" style={{ background: 'linear-gradient(135deg, #EDE9FE 0%, #F5F3FF 40%, #EDE9FE 100%)' }}>

      {/* Dot grid pattern */}
      <div
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          backgroundImage: 'radial-gradient(circle, #C4B5FD 1.2px, transparent 1.2px)',
          backgroundSize: '28px 28px',
          opacity: 0.45,
        }}
      />

      {/* Top-right soft blob */}
      <div
        className="fixed top-0 right-0 pointer-events-none z-0"
        style={{
          width: '520px',
          height: '520px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(167,139,250,0.35) 0%, transparent 70%)',
          transform: 'translate(30%, -30%)',
        }}
      />

      {/* Bottom-left soft blob */}
      <div
        className="fixed bottom-0 left-0 pointer-events-none z-0"
        style={{
          width: '480px',
          height: '480px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(196,181,253,0.3) 0%, transparent 70%)',
          transform: 'translate(-30%, 30%)',
        }}
      />

      {/* Navbar */}
      <header className="sticky top-0 z-50" style={{ background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(196,181,253,0.3)', boxShadow: '0 1px 20px rgba(139,92,246,0.08)' }}>
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between">

            {/* Logo */}
            <Link to="/" className="nav-link flex items-center gap-2">
              <img
                src={`${process.env.PUBLIC_URL}/${lang === 'ar' ? 'ticketaty-logo-ar.png' : 'ticketaty-logo.svg'}`}
                className="h-8 w-auto"
                alt={lang === 'ar' ? 'تيكيتاتي' : 'Ticketaty'}
              />
            </Link>

            {/* Desktop nav links */}
            <div className="hidden md:flex items-center space-x-1">
              <Link to="/events" className="nav-link px-4 py-2 rounded-lg text-gray-600 hover:text-purple-600 hover:bg-purple-50 font-medium text-sm transition-all">
                {t('events')}
              </Link>
              {isAuthenticated && isUser && (
                <>
                  <Link to="/my-tickets" className="nav-link px-4 py-2 rounded-lg text-gray-600 hover:text-purple-600 hover:bg-purple-50 font-medium text-sm transition-all">
                    {t('myTickets')}
                  </Link>
                  <Link to="/my-orders" className="nav-link px-4 py-2 rounded-lg text-gray-600 hover:text-purple-600 hover:bg-purple-50 font-medium text-sm transition-all">
                    {t('myOrders')}
                  </Link>
                </>
              )}
            </div>

            {/* Desktop auth */}
            <div className="hidden md:flex items-center gap-3">
              <LocaleSwitch />
              {isAuthenticated ? (
                <>
                  {isUser && (
                    <Link to="/profile" className="nav-link px-4 py-2 rounded-lg text-gray-600 hover:text-purple-600 hover:bg-purple-50 font-medium text-sm transition-all">
                      {user?.first_name || t('profile')}
                    </Link>
                  )}
                  {(isAdmin) && (
                    <Link to="/admin/dashboard" className="nav-link px-4 py-2 rounded-lg text-gray-600 hover:text-purple-600 hover:bg-purple-50 font-medium text-sm transition-all">
                      {t('dashboard')}
                    </Link>
                  )}
                  {(isOrganizer) && (
                    <Link to="/organizer/dashboard" className="nav-link px-4 py-2 rounded-lg text-gray-600 hover:text-purple-600 hover:bg-purple-50 font-medium text-sm transition-all">
                      {t('dashboard')}
                    </Link>
                  )}
                  {(isAgent) && (
                    <Link to="/agent/dashboard" className="nav-link px-4 py-2 rounded-lg text-gray-600 hover:text-purple-600 hover:bg-purple-50 font-medium text-sm transition-all">
                      {t('dashboard')}
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="px-5 py-2 rounded-xl text-sm font-semibold text-white transition-all"
                    style={{ background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)', boxShadow: '0 4px 14px rgba(139,92,246,0.35)' }}
                  >
                    {t('logout')}
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="nav-link px-4 py-2 rounded-lg text-gray-600 hover:text-purple-600 font-medium text-sm transition-all">
                    {t('login')}
                  </Link>
                  <Link
                    to="/register"
                    className="nav-link px-5 py-2 rounded-xl text-sm font-semibold text-white transition-all"
                    style={{ background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)', boxShadow: '0 4px 14px rgba(139,92,246,0.35)' }}
                  >
                    {t('signUp')}
                  </Link>
                </>
              )}
            </div>

            {/* Burger */}
            <button className="md:hidden p-2 rounded-lg hover:bg-purple-50" onClick={() => setMenuOpen(!menuOpen)}>
              <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d={menuOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'} />
              </svg>
            </button>
          </div>

          {/* Mobile menu */}
          {menuOpen && (
            <div className="md:hidden mt-3 pb-3 border-t border-purple-100 pt-3 space-y-1">
              <div className="px-4 py-2"><LocaleSwitch /></div>
              <Link to="/events" onClick={() => setMenuOpen(false)} className="block nav-link px-4 py-2 rounded-lg text-gray-700 hover:bg-purple-50 hover:text-purple-600 font-medium text-sm">{t('events')}</Link>
              {isAuthenticated && isUser && (
                <>
                  <Link to="/my-tickets" onClick={() => setMenuOpen(false)} className="block nav-link px-4 py-2 rounded-lg text-gray-700 hover:bg-purple-50 hover:text-purple-600 font-medium text-sm">{t('myTickets')}</Link>
                  <Link to="/my-orders" onClick={() => setMenuOpen(false)} className="block nav-link px-4 py-2 rounded-lg text-gray-700 hover:bg-purple-50 hover:text-purple-600 font-medium text-sm">{t('myOrders')}</Link>
                </>
              )}
              {isAuthenticated ? (
                <>
                  {isUser && <Link to="/profile" onClick={() => setMenuOpen(false)} className="block nav-link px-4 py-2 rounded-lg text-gray-700 hover:bg-purple-50 hover:text-purple-600 font-medium text-sm">{user?.first_name || t('profile')}</Link>}
                  <button onClick={() => { handleLogout(); setMenuOpen(false); }} className="w-full text-left px-4 py-2 rounded-lg text-white font-semibold text-sm mt-2" style={{ background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)' }}>{t('logout')}</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMenuOpen(false)} className="block nav-link px-4 py-2 rounded-lg text-gray-700 hover:bg-purple-50 hover:text-purple-600 font-medium text-sm">{t('login')}</Link>
                  <Link to="/register" onClick={() => setMenuOpen(false)} className="block nav-link px-4 py-2 rounded-lg text-white font-semibold text-sm mt-2" style={{ background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)' }}>{t('signUp')}</Link>
                </>
              )}
            </div>
          )}
        </nav>
        <div id="queue-banner-slot" />
      </header>

      {/* Page content */}
      <main className="relative z-10">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="relative z-10 mt-16" style={{ background: 'linear-gradient(135deg, #1E1B4B, #111827)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
            <div>
              <h3 className="text-lg font-medium mb-3 text-white">Ticketaty</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{t('discover')}</p>
              <a href="https://ticketaty.io" target="_blank" rel="noopener noreferrer" className="inline-block mt-3 text-sm text-purple-300 hover:text-white">ticketaty.io</a>
            </div>
            <div>
              <h4 className="font-medium mb-3 text-white text-sm uppercase tracking-wider">{t('customers')}</h4>
              <ul className="space-y-2">
                <li><Link to="/events" className="text-gray-400 hover:text-purple-300 text-sm transition-colors">{t('browseEvents')}</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-purple-300 text-sm transition-colors">{t('terms')}</Link></li>
                <li><Link to="/privacy" className="text-gray-400 hover:text-purple-300 text-sm transition-colors">{t('privacy')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-3 text-white text-sm uppercase tracking-wider">{t('organizers')}</h4>
              <ul className="space-y-3">
                <li><Link to="/organizer/login" className="text-gray-400 hover:text-purple-300 text-sm transition-colors">{t('organizerPortal')}</Link></li>
                <li>
                  <a href="https://talencia.io" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-purple-300 text-sm transition-colors">Talencia</a>
                  <p className="text-gray-500 text-xs mt-1">{t('talenciaBlurb')}</p>
                </li>
                <li>
                  <a href="https://camup.ai" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-purple-300 text-sm transition-colors">CamupAI</a>
                  <p className="text-gray-500 text-xs mt-1">{t('camupBlurb')}</p>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-3 text-white text-sm uppercase tracking-wider">{t('contact')}</h4>
              <p className="text-gray-400 text-sm">{t('phoneWhatsapp')}</p>
              {phoneShown ? (
                <a href="https://wa.me/97477363006" target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-sm text-white hover:text-purple-300">+974 77363006</a>
              ) : (
                <button type="button" onClick={() => setPhoneShown(true)} className="mt-2 text-sm text-purple-300 hover:text-white">
                  {t('tapToReveal')}
                </button>
              )}
            </div>
          </div>
          <div className="border-t border-white/10 mt-10 pt-6 text-center text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} Ticketaty. {t('rights')}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UserLayout;
